/* ============================================================================
   Sonna — THEME  (Shopify, wire-only: the DOM is server-rendered by Liquid).
   No build step, no render path. Keeps the original interactions + motion init
   from the static site, made scope-able so the theme editor can re-wire one
   section on `shopify:section:load` without breaking GSAP.
   Email signup endpoint is injected as window.SONNA_SHEET_URL by theme.liquid.
   ============================================================================ */
(function () {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Accordions (FAQ) ---------- */
  function wireAccordions(root) {
    (root || document).querySelectorAll('[data-accordion]').forEach((acc) => {
      if (acc.dataset.wired) return; acc.dataset.wired = '1';
      acc.addEventListener('click', (e) => {
        const q = e.target.closest('.faq__q'); if (!q) return;
        q.setAttribute('aria-expanded', q.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      });
    });
  }

  /* ---------- Product gallery (arrows / dots / thumbs / swipe / keys) ---------- */
  function wireGallery(root) {
    const gal = (root || document).querySelector('[data-gallery]'); if (!gal || gal.dataset.wired) return; gal.dataset.wired = '1';
    const main = gal.querySelector('[data-gallery-main]');
    const thumbs = Array.from(gal.querySelectorAll('[data-gallery-thumb]'));
    const dots = Array.from(gal.querySelectorAll('.gallery__dot'));
    const strip = gal.querySelector('[data-gallery-thumbs]');
    if (!main || !thumbs.length) return;
    let cur = 0;
    const setActive = (idx) => {
      cur = (idx + thumbs.length) % thumbs.length;
      thumbs.forEach((t, i) => { const on = i === cur; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on ? 'true' : 'false'); });
      dots.forEach((d, i) => d.classList.toggle('is-active', i === cur));
      const p = thumbs[cur].querySelector('.ph');
      if (p) { const c = p.cloneNode(true); c.classList.add('ph--tall'); main.innerHTML = ''; main.appendChild(c); }
      if (strip) { const t = thumbs[cur]; strip.scrollTo({ left: t.offsetLeft - strip.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' }); }
    };
    thumbs.forEach((btn, i) => btn.addEventListener('click', () => setActive(i)));
    const prev = gal.querySelector('[data-gallery-prev]');
    const next = gal.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', () => setActive(cur - 1));
    if (next) next.addEventListener('click', () => setActive(cur + 1));
    gal.addEventListener('keydown', (e) => { if (e.key === 'ArrowLeft') { e.preventDefault(); setActive(cur - 1); } else if (e.key === 'ArrowRight') { e.preventDefault(); setActive(cur + 1); } });
    const stage = gal.querySelector('.gallery__stage'); let sx = 0, sy = 0;
    if (stage) {
      stage.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
      stage.addEventListener('touchend', (e) => { const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy; if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) setActive(cur + (dx < 0 ? 1 : -1)); }, { passive: true });
    }
  }

  /* ---------- Email signup -> Google Sheet (unchanged behavior) ---------- */
  function wireSignup(root) {
    const sheetUrl = window.SONNA_SHEET_URL || '';
    (root || document).querySelectorAll('[data-signup]').forEach((form) => {
      if (form.dataset.wired) return; form.dataset.wired = '1';
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('input[type="email"]');
        const thanks = form.parentElement.querySelector('[data-signup-thanks]');
        if (!input || !/.+@.+\..+/.test(input.value)) { if (thanks) thanks.textContent = 'Please enter a valid email.'; return; }
        const email = input.value.trim();
        if (sheetUrl) {
          fetch(sheetUrl, { method: 'POST', mode: 'no-cors', body: new URLSearchParams({ email: email, source: location.pathname }) }).catch(() => {});
        }
        if (thanks) thanks.textContent = "Thanks — you're on the list. We'll be in touch.";
        input.value = '';
      });
    });
  }

  /* ---------- Sticky CTA: show after the hero ---------- */
  function initStickyCTA() {
    const bar = document.querySelector('[data-sticky-cta]'); if (!bar || bar.dataset.wired) return; bar.dataset.wired = '1';
    const onScroll = () => bar.classList.toggle('is-visible', window.scrollY > 700);
    window.addEventListener('scroll', onScroll, { passive: true }); onScroll();
  }

  /* ---------- Header: hairline + solidify on scroll, transparent over hero ---------- */
  function initHeader() {
    const wrap = document.querySelector('[data-component="header"]'); if (!wrap || wrap.dataset.wired) return; wrap.dataset.wired = '1';
    const hasHero = !!document.querySelector('.es-hero');
    if (hasHero) wrap.classList.add('has-hero');
    let ticking = false;
    const update = () => { const threshold = hasHero ? window.innerHeight * 0.8 : 16; wrap.classList.toggle('is-scrolled', window.scrollY > threshold); ticking = false; };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
    // mobile menu toggle
    const toggle = wrap.querySelector('[data-nav-toggle]');
    const mnav = wrap.querySelector('[data-mobile-nav]');
    if (toggle && mnav) {
      const setOpen = (open) => { wrap.classList.toggle('menu-open', open); mnav.classList.toggle('is-open', open); toggle.setAttribute('aria-expanded', open); document.documentElement.style.overflow = open ? 'hidden' : ''; };
      toggle.addEventListener('click', () => setOpen(!mnav.classList.contains('is-open')));
      mnav.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setOpen(false)));
      window.addEventListener('keydown', (e) => { if (e.key === 'Escape' && mnav.classList.contains('is-open')) setOpen(false); });
    }
  }

  /* ---------- Scroll-driven clay "dawn" glow (sets --dawn on :root) ---------- */
  function initDawn() {
    if (reduce) return;
    if (document.querySelector('.dawn-wash')) return;
    const wash = document.createElement('div'); wash.className = 'dawn-wash'; wash.setAttribute('aria-hidden', 'true');
    document.body.appendChild(wash);
    const root = document.documentElement; let ticking = false;
    const upd = () => { const max = root.scrollHeight - window.innerHeight; root.style.setProperty('--dawn', (max > 0 ? window.scrollY / max : 0).toFixed(3)); ticking = false; };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(upd); } }, { passive: true });
    window.addEventListener('resize', upd, { passive: true }); upd();
  }

  /* ---------- Switcher (click a title to change the panel) ---------- */
  function initSwitcher(root) {
    (root || document).querySelectorAll('[data-switch]').forEach((sec) => {
      if (sec.dataset.wired) return; sec.dataset.wired = '1';
      const titles = Array.from(sec.querySelectorAll('[data-switch-btn]'));
      const panels = Array.from(sec.querySelectorAll('[data-switch-panel]'));
      const desc = sec.querySelector('[data-switch-desc]');
      let current = -1;
      const setActive = (idx) => {
        if (idx === current) return; current = idx;
        titles.forEach((t, i) => { t.classList.toggle('is-active', i === idx); t.setAttribute('aria-selected', i === idx ? 'true' : 'false'); });
        panels.forEach((p, i) => p.classList.toggle('is-active', i === idx));
        if (desc && titles[idx]) desc.textContent = titles[idx].getAttribute('data-desc') || '';
      };
      setActive(0);
      titles.forEach((t, i) => {
        t.addEventListener('click', () => setActive(i));
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); (titles[i + 1] || titles[0]).focus(); setActive(titles[i + 1] ? i + 1 : 0); }
          else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); (titles[i - 1] || titles[titles.length - 1]).focus(); setActive(titles[i - 1] ? i - 1 : titles.length - 1); }
        });
      });
    });
  }

  /* ---------- Results: scroll-driven rail fill + count-up ---------- */
  function initResults(root) {
    (root || document).querySelectorAll('[data-results]').forEach((sec) => {
      if (sec.dataset.wired) return; sec.dataset.wired = '1';
      const fill = sec.querySelector('.results__fill');
      const rows = Array.prototype.slice.call(sec.querySelectorAll('[data-results-row]'));
      let ticking = false;
      const update = () => {
        ticking = false;
        const r = sec.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const ref = vh * 0.62;
        let prog = (ref - r.top) / Math.max(1, r.height * 0.86);
        prog = Math.max(0, Math.min(1, prog));
        if (fill && !reduce) fill.style.transform = 'scaleY(' + prog.toFixed(3) + ')';
        rows.forEach((row) => { const rr = row.getBoundingClientRect(); row.classList.toggle('is-active', rr.top + 30 < ref); });
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      window.addEventListener('resize', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
      if (!reduce && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((ents) => ents.forEach((e) => { if (e.isIntersecting) { countUp(e.target); io.unobserve(e.target); } }), { threshold: 0.6 });
        sec.querySelectorAll('[data-count]').forEach((el) => io.observe(el));
      }
    });
  }
  function countUp(el) {
    const raw = el.getAttribute('data-count') || el.textContent;
    const m = raw.match(/(\d+)/); if (!m) return;
    const target = parseInt(m[1], 10);
    const pre = raw.slice(0, m.index), suf = raw.slice(m.index + m[1].length);
    let t0 = null; const dur = 1200;
    const tick = (t) => {
      if (t0 === null) t0 = t;
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = pre + Math.round(eased * target) + suf;
      if (p < 1) requestAnimationFrame(tick); else el.textContent = raw;
    };
    el.textContent = pre + '0' + suf;
    requestAnimationFrame(tick);
  }

  /* ---------- Use-case carousel: prev/next arrows ---------- */
  function wireChallenges(root) {
    (root || document).querySelectorAll('[data-challenges]').forEach((track) => {
      if (track.dataset.wired) return; track.dataset.wired = '1';
      const wrap = track.closest('.challenges-wrap') || document;
      const prev = wrap.querySelector('[data-challenges-prev]');
      const next = wrap.querySelector('[data-challenges-next]');
      const step = () => { const card = track.querySelector('.challenge'); return card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8; };
      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      const upd = () => {
        const atStart = track.scrollLeft < 8;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
        if (prev) { prev.classList.toggle('is-disabled', atStart); prev.setAttribute('aria-disabled', atStart); }
        if (next) { next.classList.toggle('is-disabled', atEnd); next.setAttribute('aria-disabled', atEnd); }
      };
      track.addEventListener('scroll', upd, { passive: true });
      window.addEventListener('resize', upd, { passive: true });
      upd();
    });
  }

  /* ---------- Hero video: keep autoplaying + looping across browsers ---------- */
  function initHeroVideo(root) {
    const v = (root || document).querySelector('.es-hero__video'); if (!v || v.dataset.wired) return; v.dataset.wired = '1';
    v.muted = true; v.defaultMuted = true; v.loop = true; v.playsInline = true;
    const tryPlay = () => { try { const p = v.play(); if (p && p.catch) p.catch(() => {}); } catch (e) {} };
    tryPlay();
    v.addEventListener('loadeddata', tryPlay, { once: true });
    v.addEventListener('canplay', tryPlay, { once: true });
    v.addEventListener('ended', () => { try { v.currentTime = 0; tryPlay(); } catch (e) {} });
    const onInteract = () => { tryPlay(); ['touchstart', 'pointerdown', 'scroll', 'click'].forEach((e) => window.removeEventListener(e, onInteract)); };
    ['touchstart', 'pointerdown', 'scroll', 'click'].forEach((e) => window.addEventListener(e, onInteract, { once: false, passive: true }));
    document.addEventListener('visibilitychange', () => { if (!document.hidden) tryPlay(); });
  }

  /* ---------- Reveals init (delegates to SonnaReveals, scope-aware) ---------- */
  function initReveals(scope) {
    if (window.SonnaReveals) { window.SonnaReveals.init(scope); return; }
    const els = (scope || document).querySelectorAll('[data-reveal]');
    if (reduce || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Fail-safe: never leave the page stuck blank ---------- */
  function revealSafetyNet() {
    const check = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      let stuck = false;
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0 && parseFloat(getComputedStyle(el).opacity) < 0.05) stuck = true;
      });
      if (!stuck) return;
      document.body.classList.remove('gsap-active');
      document.querySelectorAll('[data-reveal]').forEach((el) => { el.classList.add('is-in'); el.style.opacity = ''; el.style.transform = ''; });
      document.querySelectorAll('[data-reveal-text]').forEach((el) => { el.style.opacity = '1'; });
      document.querySelectorAll('.sl-line, .sl-word').forEach((el) => { el.style.transform = 'none'; el.style.opacity = '1'; });
      document.querySelectorAll('.gallery__main .ph, .split__media .ph, .annotated, .challenge .ph, .switch__panel').forEach((el) => { el.style.clipPath = 'none'; });
    };
    setTimeout(check, 1500);
    setTimeout(check, 3500);
  }

  /* ---------- Wire one scope (whole document, or one reloaded section) ---------- */
  /* ---------- Quantity stepper (product buy box) ---------- */
  function wireQty(root) {
    (root || document).querySelectorAll('[data-qty-form]').forEach((form) => {
      if (form.dataset.wired) return; form.dataset.wired = '1';
      const input = form.querySelector('[data-qty-input]');
      if (!input) return;
      const clamp = () => { let n = parseInt(input.value, 10); if (isNaN(n) || n < 1) n = 1; input.value = n; return n; };
      const minus = form.querySelector('[data-qty-minus]');
      const plus = form.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', () => { input.value = Math.max(1, clamp() - 1); });
      if (plus) plus.addEventListener('click', () => { input.value = clamp() + 1; });
      input.addEventListener('change', clamp);
    });
  }

  /* ---------- Cart drawer (AJAX add-to-cart + slide-out) ---------- */
  function initCart() {
    if (document.body.dataset.cartWired) return; document.body.dataset.cartWired = '1';
    const getDrawer = () => document.querySelector('[data-cart-drawer]');
    const openDrawer = () => { const d = getDrawer(); if (!d) return; d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false'); document.body.classList.add('cart-open'); };
    const closeDrawer = () => { const d = getDrawer(); if (!d) return; d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); document.body.classList.remove('cart-open'); };
    const setCount = (n) => { document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = n; if (n > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); } }); };
    const CLOSE_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
    const money = (cents) => '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    function itemRow(item, i) {
      const img = item.image ? '<img src="' + item.image + '" alt="" width="64" height="64">' : '';
      return '<div class="cart-item" data-line="' + (i + 1) + '"><div class="cart-item__media">' + img + '</div>'
        + '<div class="cart-item__info"><span class="cart-item__title">' + esc(item.product_title) + '</span>'
        + '<span class="cart-item__price">' + money(item.final_price) + '</span>'
        + '<div class="cart-item__qty"><button type="button" class="qty__btn" data-cart-minus aria-label="Decrease quantity">−</button>'
        + '<input class="qty__input" type="number" value="' + item.quantity + '" min="0" inputmode="numeric" aria-label="Quantity" data-cart-qty>'
        + '<button type="button" class="qty__btn" data-cart-plus aria-label="Increase quantity">+</button></div></div></div>';
    }
    function renderCart(cart) {
      const drawer = getDrawer(); if (!drawer) return;
      setCount(cart.item_count);
      const panel = drawer.querySelector('.cart-drawer__panel'); if (!panel) return;
      const body = panel.querySelector('.cart-drawer__body');
      let foot = panel.querySelector('.cart-drawer__foot');
      if (cart.item_count === 0) {
        if (body) body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
        if (foot && foot.parentNode) foot.parentNode.removeChild(foot);
        return;
      }
      if (body) body.innerHTML = cart.items.map(itemRow).join('');
      const footHTML = '<div class="cart-drawer__subtotal"><span>Subtotal</span><span>' + money(cart.total_price) + '</span></div>'
        + '<form action="/cart" method="post"><button type="submit" name="checkout" class="btn btn--lg btn--block">Checkout</button></form>';
      if (foot) { foot.innerHTML = footHTML; } else { foot = document.createElement('div'); foot.className = 'cart-drawer__foot'; foot.innerHTML = footHTML; panel.appendChild(foot); }
    }
    function refresh() { return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json()).then(renderCart); }

    function changeLine(line, quantity) {
      fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ line: line, quantity: quantity }) })
        .then((r) => r.json()).then(renderCart);
    }

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]')) { e.preventDefault(); openDrawer(); return; }
      if (e.target.closest('[data-cart-close]')) { closeDrawer(); return; }
      const row = e.target.closest('[data-line]'); if (!row || !e.target.closest('[data-cart-drawer]')) return;
      const line = parseInt(row.getAttribute('data-line'), 10);
      const input = row.querySelector('[data-cart-qty]');
      const cur = input ? (parseInt(input.value, 10) || 1) : 1;
      if (e.target.closest('[data-cart-remove]')) changeLine(line, 0);
      else if (e.target.closest('[data-cart-minus]')) changeLine(line, Math.max(0, cur - 1));
      else if (e.target.closest('[data-cart-plus]')) changeLine(line, cur + 1);
    });
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-qty]'); if (!input || !e.target.closest('[data-cart-drawer]')) return;
      const row = input.closest('[data-line]'); if (!row) return;
      changeLine(parseInt(row.getAttribute('data-line'), 10), Math.max(0, parseInt(input.value, 10) || 0));
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action$="/cart/add"]'); if (!form) return;
      const idEl = form.querySelector('[name="id"]'); if (!idEl) return;
      e.preventDefault();
      const qtyEl = form.querySelector('[name="quantity"]');
      const payload = { id: idEl.value, quantity: qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1 };
      fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload) })
        .then((r) => r.json())
        .then(() => refresh())
        .then(() => openDrawer())
        .catch(() => { form.submit(); });
    });
  }

  function wireSection(root) {
    wireAccordions(root);
    wireGallery(root);
    wireQty(root);
    wireSignup(root);
    initSwitcher(root);
    initResults(root);
    wireChallenges(root);
    initHeroVideo(root);
  }

  function start() {
    initHeader();
    initDawn();
    initCart();
    wireSection(document);
    initReveals(document);
    revealSafetyNet();
  }

  /* ---------- Shopify theme editor: re-wire on section load/unload/select ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    const sec = e.target;
    initStickyCTA(); initHeader();
    wireSection(sec);
    initReveals(sec);
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', () => { if (window.ScrollTrigger) window.ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:select', (e) => { if (e.target && e.target.scrollIntoView) e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
